import { useMemo, useState } from 'react'
import { z } from 'zod'
import { Code2, ListChecks, TriangleAlert } from 'lucide-react'
import type { TestRun } from '../data/projectTypes'
import { journeyToolSnapshot, toolFailureLocation, JOURNEY_STEP_TIMINGS } from '../lib/journeyToolFailure'
import { displayJourneySteps } from '../guidance/journey/template-display'
import { useAdminMode } from '../hooks/useAdminMode'
import { useGetAppArtifactsQuery } from '../services/projectDataApi'
import AppToolInvocationDetails from './AppToolInvocationDetails'
import StepDuration from './StepDuration'
import '../pages/JourneyDetail.css'

// Best-effort reading of the opaque JourneySteps aggregate. The guidance owns the representation; the
// app only knows the shape it has used so far ({ actions: [{ action, type, value, checks: [...] }] }).
// When the blob matches, the steps render as a stepper; anything else falls back to the raw JSON.
const StepCheckSchema = z.object({
  description: z.string().optional(), assertion: z.string().optional(),
  expected: z.string().optional(), selector: z.string().optional(),
})
const StepActionSchema = z.object({
  toolId: z.string().optional(), toolRevision: z.number().optional(), arguments: z.record(z.string()).optional(),
  action: z.string().optional(), type: z.string().optional(), value: z.string().optional(),
  label: z.string().optional(), keyboardEvent: z.enum(['keydown', 'keyup']).optional(),
  checks: z.array(z.union([StepCheckSchema, z.string().transform(description => StepCheckSchema.parse({ description }))])).optional(),
})
const JourneyStepsSchema = z.object({ actions: z.array(StepActionSchema).min(1) })
type StepAction = z.infer<typeof StepActionSchema>

// Authored checks the run's own record did not establish (run-journey.ts QARunResult.unestablished_checks).
const UnestablishedChecksSchema = z.object({
  unestablished_checks: z.array(z.object({ step: z.number(), check: z.string(), how: z.string() })).optional(),
})

function readSteps(steps: unknown): StepAction[] | null {
  const parsed = JourneyStepsSchema.safeParse(displayJourneySteps(steps))
  return parsed.success ? parsed.data.actions : null
}

function parseFailedStepIndex(error: string | null | undefined): { index: number; message: string } | null {
  if (!error) return null
  const m = error.match(/^(?:Check failed at|Errors detected after) step (\d+): (.+)/)
  return m ? { index: parseInt(m[1]!, 10) - 1, message: m[2]! } : null
}

export default function JourneySteps({ projectId, steps, stepCount, lastRunError, lastRunStatus, run }: { run?: Pick<TestRun, 'id'> & Partial<Pick<TestRun, 'journey_id' | 'outcome'>> & { setup_output?: unknown }; projectId: string; steps: unknown; stepCount: number; lastRunError: string | null; lastRunStatus: string | null }) {
  const [showRaw, setShowRaw] = useState(false)
  const adminMode = useAdminMode()
  const parsed = useMemo(() => readSteps(steps), [steps])
  const isFailed = lastRunStatus === 'failed' || lastRunStatus === 'infra-failed' || lastRunStatus === 'incomplete' || lastRunStatus === 'blocked'
  const toolJourney = parsed?.some(step => step.type === 'callTool') ?? false
  const artifacts = useGetAppArtifactsQuery({ projectId, runId: run?.id }, { skip: !run || !toolJourney || (!isFailed && !adminMode) })
  const savedSnapshot = useMemo(() => journeyToolSnapshot(artifacts.data, run?.id), [artifacts.data, run?.id])
  const snapshot = savedSnapshot?.journeyId === run?.journey_id
    && parsed?.length === savedSnapshot?.invocations.length
    && parsed?.every((step, index) => step.toolId === savedSnapshot?.invocations[index]?.toolId)
    ? savedSnapshot : null
  const storedExecution = JOURNEY_STEP_TIMINGS.safeParse(run?.setup_output)
  const stepTimings = snapshot?.steps ?? (storedExecution.success ? storedExecution.data.steps ?? [] : [])
  const failedStepIndex = run?.outcome?.stepIndex
  const toolFailure = isFailed ? toolFailureLocation(snapshot, failedStepIndex) : null
  const failure = isFailed && !toolJourney
    ? (failedStepIndex !== undefined ? { index: failedStepIndex, message: lastRunError } : parseFailedStepIndex(lastRunError)) : null
  const failureMessage = failure?.message ?? (isFailed ? lastRunError : null)
  const unestablishedResult = UnestablishedChecksSchema.safeParse(run?.setup_output)
  const unestablished = unestablishedResult.success ? unestablishedResult.data.unestablished_checks ?? [] : []
  let serialized: string
  try { serialized = JSON.stringify(steps, null, 2) } catch { serialized = String(steps) }

  return (
    <section className="journey-detail__card" data-testid="journey-detail-steps">
      <div className="journey-detail__card-head">
        <h2 className="journey-detail__card-title">
          {toolJourney ? 'App tools' : 'Steps'}
          <span className="journey-detail__count">{stepCount}</span>
        </h2>
        {adminMode && <button
          type="button"
          className="journey-detail__raw-toggle"
          onClick={() => setShowRaw(v => !v)}
          aria-expanded={showRaw}
          data-testid="journey-steps-toggle"
          data-analytics-event="journey-steps-toggle"
        >
          <Code2 size={13} aria-hidden /> {showRaw ? 'Hide JSON' : 'JSON'}
        </button>}
      </div>
      {failureMessage && !failure && !toolFailure && (
        <div className="journey-detail__run-error" data-testid="run-error-banner">
          <TriangleAlert size={14} aria-hidden />
          <span>{failureMessage}</span>
        </div>
      )}
      {unestablished.length > 0 && (
        <p className="journey-detail__muted" data-testid="unestablished-checks">
          Not established by this run: {unestablished.map(c => `step ${c.step} “${c.check}” (${c.how})`).join('; ')}
        </p>
      )}
      {isFailed && toolJourney && run && !toolFailure && (
        <p className="journey-detail__muted">{artifacts.isFetching ? 'Locating the step where this run stopped…' : 'The exact tool step is unavailable for this run.'}</p>
      )}
      {parsed ? (
        <ol className="journey-detail__steps">
          {parsed.map((step, idx) => {
            const failedHere = failure?.index === idx || toolFailure?.invocationIndex === idx
            const invocation = snapshot?.invocations[idx]
            const title = step.action ?? step.type ?? `Step ${idx + 1}`
            const eventType = step.type === 'pressKey' && step.keyboardEvent
              ? (step.keyboardEvent === 'keydown' ? 'Key down' : 'Key up') : step.type
            const value = step.value === ' ' ? 'Space' : step.value
            const detail = eventType && value ? `${eventType} · ${value}` : value ?? (eventType && eventType !== title ? eventType : null)
            return (
              <li key={idx} className={`journey-detail__step${failedHere ? ' journey-detail__step--failed' : ''}`} data-testid={`journey-step-${idx}`}>
                <p className="journey-detail__step-title">{title}</p>
                {adminMode && run && <StepDuration durationMs={step.type === 'callTool' ? invocation?.duration_ms : stepTimings.find(result => result.index === idx)?.duration_ms} total={step.type === 'callTool'} />}
                {step.type === 'callTool' && step.toolId && <AppToolInvocationDetails projectId={projectId} runId={run?.id} toolId={step.toolId} revision={invocation?.revision ?? step.toolRevision} arguments={step.arguments}
                  snapshotTool={snapshot?.tools.find(tool => tool.id === step.toolId)}
                  stepTimings={invocation ? stepTimings.filter(result => result.index >= invocation.firstStep && result.index <= invocation.lastStep).map(result => ({ ...result, index: result.index - invocation.firstStep })) : undefined}
                  failure={failedHere && toolFailure ? { stepIndex: toolFailure.stepIndex, message: failureMessage ?? 'The run stopped here.', status: lastRunStatus ?? 'incomplete' } : undefined} />}
                {step.type !== 'callTool' && detail && <p className="journey-detail__step-detail">{detail}</p>}
                {step.checks && step.checks.length > 0 && (
                  <ul className="journey-detail__checks">
                    {step.checks.map((check, cIdx) => (
                      <li key={cIdx} className="journey-detail__check">
                        <ListChecks size={12} aria-hidden />
                        <span>{check.description ?? check.assertion ?? 'Check'}</span>
                        {check.selector && <code>{check.selector}</code>}
                      </li>
                    ))}
                  </ul>
                )}
                {failedHere && failure && (
                  <div className="journey-detail__run-error" data-testid="run-error-banner">
                    <TriangleAlert size={14} aria-hidden />
                    <span>{failure.message}</span>
                  </div>
                )}
              </li>
            )
          })}
        </ol>
      ) : !showRaw && (
        <p className="journey-detail__muted">These steps use a representation the app does not render yet. Open the JSON to inspect them.</p>
      )}
      {adminMode && showRaw && <pre className="journey-detail__steps-json" data-testid="journey-steps-aggregate">{serialized}</pre>}
    </section>
  )
}
